import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["tab", "panel", "mobile"]
  static values = { active: Number }

  connect() {
    this.sync()
    const hash = (window.location.hash || "").replace("#", "")
    let index = this.ids.indexOf(hash)
    if (index < 0) index = this.activeValue || 0
    this.show(index, { updateHash: false })
  }

  disconnect() {
    this.ids = []
    this.names = []
  }

  activate({ params, currentTarget }) {
    let index = parseInt(params.index ?? currentTarget.dataset.tabsIndex, 10)
    if (Number.isNaN(index)) index = 0
    this.show(index)
  }

  select(event) {
    const index = parseInt(event.target.value, 10)
    if (Number.isNaN(index)) return
    this.show(index)
  }

  show(index, { updateHash = true } = {}) {
    this.sync()
    if (index < 0 || index >= this.panelTargets.length) return

    this.activeValue = index

    this.panelTargets.forEach((panel, idx) => {
      panel.classList.toggle("hidden", idx !== index)
    })

    this.tabTargets.forEach((tab, idx) => {
      const isActive = idx === index
      tab.setAttribute("aria-selected", String(isActive))

      const activeClasses = this.classListFromData(tab.dataset.tabsActiveClass, ["border-yellow-700", "text-slate-900"])
      const inactiveClasses = this.classListFromData(tab.dataset.tabsInactiveClass, ["border-transparent", "text-slate-600"])

      activeClasses.forEach(cls => tab.classList.toggle(cls, isActive))
      inactiveClasses.forEach(cls => tab.classList.toggle(cls, !isActive))
    })

    if (this.hasMobileTarget) this.mobileTarget.value = String(index)

    if (updateHash) {
      const id = this.ids[index]
      if (id) {
        const url = new URL(window.location.href)
        url.hash = id
        history.replaceState(null, "", url)
      }
    }

    const detail = { index, id: this.ids[index], name: this.names[index] }
    this.element.dispatchEvent(new CustomEvent("tabs:change", { detail, bubbles: true }))
  }

  sync() {
    this.ids = this.panelTargets.map(panel => panel.id)
    this.names = this.tabTargets.map(tab => tab.dataset.tabsName || "")
  }

  classListFromData(value, fallback) {
    const classes = (value || "").trim().split(/\s+/).filter(Boolean)
    return classes.length > 0 ? classes : fallback
  }
}
